import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddDirectMailPopup from "./DirectMail/AddDirectMailPopup";
import DirectMailRightDrawer from "./DirectMail/DirectMailRightDrawer";
import DirectMailBottomDrawer from "./DirectMail/DirectMailBottomDrawer";
import DirectMailDetail from "./DirectMail/DirectMailDetail";
import DirectMailEmptyPage from "./DirectMail/DirectMailEmptyPage";

import { directMailAPI } from "api/Interactions/DirectMailAPI";
import { DirectMail } from "api/Interactions/InteractionsInterfaces";

const DirectMailPage: React.FC = () => {
  const [directMailData, setDirectMailData] = useState<DirectMail[]>([]);
  const [selectedDirectMail, setSelectedDirectMail] = useState<DirectMail | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  useEffect(() => {
    directMailAPI.getAll()
      .then(setDirectMailData)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeId) {
      setSelectedDirectMail(null);
      return;
    }

    directMailAPI.getById(activeId)
      .then(setSelectedDirectMail)
      .catch(() => setSelectedDirectMail(null));
  }, [activeId]);

  const handleSelectDirectMail = (id: number) => {
    navigate(`/interactions/direct-mail/${id}`);
  };

  if (loading)
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}><CircularProgress /></Box>;

  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft={true}
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<DirectMail>
          items={directMailData}
          selectedId={activeId ?? undefined}
          onSelect={(item) => handleSelectDirectMail(item.id)}
          AddPopupComponent={AddDirectMailPopup}
          onCreated={(newItem) => {
            setDirectMailData((prev) => [...prev, newItem]);
            navigate(`/interactions/direct-mail/${newItem.id}`);
          }}
          idKey="id"
          nameKey={(m) => m.name || `Direct Mail ${m.id}`}
          colorKey="interactionsPrimary"
          searchPlaceholder="Search direct mail..."
        />
      }
      rightDrawerContent={activeId ? <DirectMailRightDrawer directMailId={activeId} /> : null}
      bottomDrawerContent={activeId ? <DirectMailBottomDrawer directMailId={activeId} /> : null}
    >
      {selectedDirectMail ? (
        <DirectMailDetail
          directMailId={activeId!}
          onClose={() => navigate("/interactions/direct-mail/")}
        />
      ) : (
        <DirectMailEmptyPage
          onDirectMailCreated={(newItem) => navigate(`/interactions/direct-mail/${newItem.id}`)}
        />
      )}
    </GeneralLayout>
  );
};

export default DirectMailPage;
